import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Bus, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import DestinationAutocomplete from '../components/DestinationAutocomplete';
import BookingConfirmation from '../components/BookingConfirmation';
import BookingModal from '../components/BookingModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BusesPage() {
  const [searchData, setSearchData] = useState({
    from_city: '',
    to_city: '',
    travel_date: '',
    passengers: 1
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedBus, setSelectedBus] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchData.from_city || !searchData.to_city || !searchData.travel_date) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      setLoading(true);
      const res = await axios.post(`${API}/buses/search`, searchData);
      setResults(res.data);
      setSearched(true);
    } catch (err) {
      toast.error('Error searching buses');
    } finally {
      setLoading(false);
    }
  };

  const openBookingModal = (bus) => {
    setSelectedBus(bus);
    setShowBookingModal(true);
  };

  const handleBookingSubmit = async (customerData) => {
    if (!selectedBus) return;

    try {
      setBookingLoading(true);
      const res = await axios.post(`${API}/bookings/create`, {
        booking_type: 'bus',
        customer_name: customerData.name,
        customer_mobile: customerData.mobile,
        customer_email: customerData.email || '',
        from_location: selectedBus.from_city,
        to_location: selectedBus.to_city,
        travel_date: searchData.travel_date,
        travelers: searchData.passengers,
        provider_name: selectedBus.operator,
        departure_time: selectedBus.departure_time,
        arrival_time: selectedBus.arrival_time,
        duration: selectedBus.duration,
        class_type: selectedBus.bus_type,
        price: selectedBus.price * searchData.passengers
      });
      
      setShowBookingModal(false);
      setBookingDetails({
        bookingId: res.data.booking_id,
        name: `${selectedBus.operator} - ${selectedBus.bus_type}`,
        details: `${selectedBus.from_city} → ${selectedBus.to_city}`,
        date: searchData.travel_date,
        travelers: searchData.passengers,
        price: selectedBus.price * searchData.passengers
      });
      setShowConfirmation(true);
    } catch (err) {
      toast.error('Error creating booking');
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-20" data-testid="buses-page">
      {/* Hero */}
      <section className="relative h-64 md:h-80 bg-gradient-to-r from-green-600 to-green-800 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=1920"
            alt="Buses"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 mb-4"
          >
            <Bus className="w-12 h-12 text-white" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-medium text-white mb-4"
          >
            Book Buses
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-green-100"
          >
            Comfortable bus travel across India
          </motion.p>
        </div>
      </section>

      {/* Search Form */}
      <section className="max-w-4xl mx-auto px-6 -mt-12 relative z-20">
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleSearch}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
          data-testid="bus-search-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <DestinationAutocomplete
              label="From City"
              value={searchData.from_city}
              onChange={(val) => setSearchData({ ...searchData, from_city: val })}
              placeholder="Bangalore"
            />
            <DestinationAutocomplete
              label="To City"
              value={searchData.to_city}
              onChange={(val) => setSearchData({ ...searchData, to_city: val })}
              placeholder="Chennai"
            />
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Travel Date</label>
              <input
                type="date"
                value={searchData.travel_date}
                onChange={(e) => setSearchData({ ...searchData, travel_date: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none"
                data-testid="travel-date"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Passengers</label>
              <select
                value={searchData.passengers}
                onChange={(e) => setSearchData({ ...searchData, passengers: parseInt(e.target.value) })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none bg-white"
                data-testid="passengers"
              >
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>{n} {n === 1 ? 'Passenger' : 'Passengers'}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-6 flex justify-center">
            <Button
              type="submit"
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white rounded-full px-12 py-6 text-lg font-medium"
              data-testid="search-buses-btn"
            >
              {loading ? 'Searching...' : 'Search Buses'}
            </Button>
          </div>
        </motion.form>
      </section>

      {/* Discount Banner */}
      <section className="max-w-4xl mx-auto px-6 mt-8">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 flex items-center gap-3">
          <Info className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-800">
            <span className="font-semibold">Special Bus Discounts!</span> Book through ID4U Travels and save more on your journey.
          </p>
        </div>
      </section>

      {/* Results */}
      {searched && (
        <section className="max-w-4xl mx-auto px-6 py-12" data-testid="bus-results">
          <h2 className="text-2xl font-semibold text-slate-900 mb-6">
            {results.length > 0 ? `${results.length} Buses Found` : 'No Buses Found'}
          </h2>
          <div className="space-y-4">
            {results.map((bus, index) => (
              <motion.div
                key={bus.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-slate-100"
                data-testid={`bus-result-${index}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <Bus className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{bus.operator}</h3>
                      <p className="text-sm text-slate-500">{bus.bus_type}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-8">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-900">{bus.departure_time}</p>
                      <p className="text-sm text-slate-500">{bus.from_city}</p>
                    </div>
                    <div className="flex flex-col items-center">
                      <p className="text-xs text-slate-400 mb-1">{bus.duration}</p>
                      <div className="w-20 h-px bg-slate-300 relative">
                        <Bus className="w-4 h-4 text-green-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-900">{bus.arrival_time}</p>
                      <p className="text-sm text-slate-500">{bus.to_city}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">₹{bus.price?.toLocaleString()}</p>
                      <span className="text-xs text-slate-500">{bus.seats_available} seats left</span>
                    </div>
                    <Button
                      onClick={() => openBookingModal(bus)}
                      className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                      data-testid={`book-bus-${index}`}
                    >
                      Book Now
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Booking Modal */}
      <AnimatePresence>
        {showBookingModal && (
          <BookingModal
            isOpen={showBookingModal}
            onClose={() => setShowBookingModal(false)}
            onSubmit={handleBookingSubmit}
            title="Book Your Bus Ticket"
            loading={bookingLoading}
          />
        )}
      </AnimatePresence>

      {/* Booking Confirmation Modal */}
      <AnimatePresence>
        {showConfirmation && (
          <BookingConfirmation
            bookingType="bus"
            bookingDetails={bookingDetails}
            onClose={() => setShowConfirmation(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
